import React from "react";
import { Sparkles, Compass, Shield, Fingerprint, Search } from "lucide-react";

const STAGES = [
  { label: 'Initializing Neural Path', detail: 'Connecting to trusted global sources...', icon: Compass },
  { label: 'Refining Perspective', detail: 'Removing noise and identifying patterns...', icon: Search },
  { label: 'Confirming Authenticity', detail: 'Verifying claims with precision...', icon: Shield },
  { label: 'Aligning Data Layers', detail: 'Structuring the intelligence report...', icon: Fingerprint },
  { label: 'Synthesizing Truth', detail: 'Almost ready for presentation...', icon: Sparkles },
];

export default function AnalysisAnimation({ stage }) {
  const current = STAGES[stage % STAGES.length];
  const progress = Math.min(99, Math.round(((stage + 1) / STAGES.length) * 100));

  return (
    <div className="fixed inset-0 bg-ink-base/95 backdrop-blur-md z-[100] flex items-center justify-center overflow-hidden">
      {/* Background Atmosphere */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand/5 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] bg-brand/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative flex flex-col items-center max-w-sm w-full px-8 text-center">
        {/* Elegant Branded Visualization */}
        <div className="relative w-40 h-40 mb-12">
          {/* Outer Glass Ring */}
          <div className="absolute inset-0 rounded-full border border-brand/20 backdrop-blur-[2px]" />
          
          {/* Smooth Progress Ring */}
          <svg className="w-full h-full -rotate-90">
            <circle
              cx="80"
              cy="80"
              r="76"
              fill="none"
              stroke="url(#brand-gradient)"
              strokeWidth="2.5"
              strokeDasharray={477}
              strokeDashoffset={477 - (477 * progress) / 100}
              className="transition-all duration-1000 ease-in-out"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id="brand-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#f5b041" />
                <stop offset="100%" stopColor="#e67e22" />
              </linearGradient>
            </defs>
          </svg>

          {/* Liquid Core */}
          <div className="absolute inset-4 rounded-full flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-brand/10 to-brand/5" />
            <div className="relative z-10">
              <current.icon size={42} className="text-brand drop-shadow-[0_0_15px_rgba(245,176,65,0.5)] animate-bounce-subtle" />
            </div>
            
            {/* Shifting Glow Particles */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-from)_0%,_transparent_70%)] from-brand/10 animate-pulse" />
          </div>
        </div>

        {/* Textual Branding */}
        <div className="space-y-2 mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/10 border border-brand/20 mb-2">
            <div className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-brand">N.E.T.R.A Analysis</span>
          </div>
          
          <h2 className="text-2xl font-semibold text-white tracking-tight">
            {current.label}
          </h2>
          <p className="text-zinc-400 text-sm font-medium leading-relaxed px-4">
            {current.detail}
          </p>
        </div>

        {/* Minimalist Progress Track */}
        <div className="w-full space-y-4">
          <div className="h-1 w-full bg-ink-elevated rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-brand to-[#e67e22] transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(245,176,65,0.4)]"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          <div className="flex justify-between items-center px-1">
            <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">Progress</span>
            <span className="text-[11px] font-bold text-brand font-mono tracking-tighter">{progress}%</span>
          </div>
        </div>

        {/* Subtle Branding Bottom */}
        <div className="mt-12 pt-8 border-t border-ink-border-subtle w-full">
          <p className="text-[10px] text-zinc-600 uppercase tracking-[0.4em] font-bold">
            Horizon Flare India
          </p>
        </div>
      </div>

      <style jsx="true">{`
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
